import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircleQuestion, CornerDownRight } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QAPanel({ tender, role }) {
  const [questions, setQuestions] = useState([]);
  const [text, setText] = useState("");
  const [answerDrafts, setAnswerDrafts] = useState({});

  const load = useCallback(() => {
    axios.get(`${API}/tenders/${tender.id}/questions`).then((r) => setQuestions(r.data));
  }, [tender.id]);
  useEffect(() => { load(); }, [load]);

  const ask = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await axios.post(`${API}/tenders/${tender.id}/questions`, { question: text });
      toast.success("Question submitted to the procuring entity.");
      setText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit question.");
    }
  };

  const answer = async (qid) => {
    const a = (answerDrafts[qid] || "").trim();
    if (!a) return;
    try {
      await axios.post(`${API}/questions/${qid}/answer`, { answer: a });
      toast.success("Official answer published to all vendors.");
      setAnswerDrafts((d) => ({ ...d, [qid]: "" }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish answer.");
    }
  };

  const canAsk = role === "vendor" && ["published", "under_evaluation"].includes(tender.status);
  const canAnswer = role !== "vendor";

  return (
    <div className="grid lg:grid-cols-3 gap-8 items-start">
      <div className="lg:col-span-2 bg-white border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Clarifications</h3>
          <span className="font-mono text-sm text-slate-500" data-testid="question-count">{questions.length} question{questions.length !== 1 ? "s" : ""}</span>
        </div>
        {questions.length === 0 ? (
          <p className="text-sm text-slate-400 p-10 text-center" data-testid="qa-empty-state">No clarification questions yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {questions.map((q, i) => (
              <div key={q.id} className="p-6" data-testid={`question-row-${i}`}>
                <div className="flex items-start gap-3">
                  <MessageCircleQuestion className="w-4 h-4 text-[#0050A0] mt-1 shrink-0" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-900 leading-relaxed">{q.question}</p>
                    <p className="text-xs text-slate-400 mt-1.5">Asked by {q.asked_by} · {q.created_at?.slice(0, 10)}</p>
                    {q.answer ? (
                      <div className="mt-4 flex items-start gap-2 bg-[#F0F7FC] border border-[#009EDB]/30 p-4" data-testid={`answer-${i}`}>
                        <CornerDownRight className="w-4 h-4 text-[#009EDB] mt-0.5 shrink-0" aria-hidden="true" />
                        <div>
                          <p className="text-sm text-slate-700 leading-relaxed">{q.answer}</p>
                          <p className="text-[11px] text-slate-400 mt-1.5 uppercase tracking-[0.15em]">Official answer · {q.answered_at?.slice(0, 10)}</p>
                        </div>
                      </div>
                    ) : canAnswer ? (
                      <div className="mt-4 space-y-2">
                        <Textarea data-testid={`answer-input-${i}`} className="bg-white border-slate-300 min-h-[70px]" placeholder="Draft the official answer…" value={answerDrafts[q.id] || ""} onChange={(e) => setAnswerDrafts((d) => ({ ...d, [q.id]: e.target.value }))} />
                        <button onClick={() => answer(q.id)} data-testid={`publish-answer-btn-${i}`} className="bg-[#0050A0] text-white px-4 py-2 text-xs font-semibold hover:bg-[#003F7A] transition-colors duration-200">
                          Publish Answer
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600 mt-3" data-testid={`awaiting-answer-${i}`}>Awaiting official answer.</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <h3 className="font-serif text-lg text-slate-900 mb-1">Ask a Question</h3>
        {!canAsk ? (
          <p className="text-xs text-slate-400 mt-3 leading-relaxed" data-testid="qa-form-disabled-note">
            {role !== "vendor" ? "Vendors ask clarification questions here; you publish the official answers below each question." : "Questions can be asked on published tenders only."}
          </p>
        ) : (
          <form onSubmit={ask} className="space-y-4 mt-4">
            <Textarea data-testid="question-input" className="bg-white border-slate-300 min-h-[110px]" placeholder="Ask about specifications, eligibility, deadlines…" value={text} onChange={(e) => setText(e.target.value)} />
            <button type="submit" data-testid="submit-question-btn" className="w-full bg-[#0050A0] text-white py-3 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200">
              Submit Question
            </button>
            <p className="text-[11px] text-slate-400 leading-relaxed">Answers are published publicly to all bidders to ensure equal treatment.</p>
          </form>
        )}
      </div>
    </div>
  );
}
